import { useEffect, useRef, useState } from 'react';
import { NotificationTextMessage } from '@/core/enums/notification';
import type { ReportDetail, SaveReportPayload } from '@/core/models/reports.models';
import { notifyBlobError, notifyError, notifySuccess } from '@/core/notify/notify';
import { reportsService } from '@/core/services/reports';
import { Button } from '@/kit/Button';
import { ConfirmPopup, type ConfirmPopupData } from '@/kit/ConfirmPopup';
import { Icon } from '@/kit/Icon';
import { DialogActions, DialogClose, DialogContent, openDialog, type DialogComponentProps } from '@/kit/dialog';
import './ReportPdfPreview.scss';

export interface ReportPdfPreviewData {
  /** A saved report, previewed as saved (the list's Ref. No. link). */
  reportId?: string;
  refNo: string;
  /**
   * BUG-048: a report not saved yet, previewed as Create New Report describes it. The preview then offers Save Report,
   * which is the only step that stores it (as the old application); closing the preview saves nothing.
   */
  draft?: SaveReportPayload;
  /** The old application's preview buttons for a saved report: Email it, and Regenerate it (true once regenerated). */
  onEmail?: () => void | Promise<void>;
  onRegenerate?: () => Promise<boolean>;
}

/**
 * The dialog options both callers use (#159: the preview uses most of the window's height). Its width is about the old
 * application's preview, so the page fitted to it shows as many lines as the old preview did instead of a magnified top.
 */
export const REPORT_PDF_PREVIEW_DIALOG = { width: '95vw', maxWidth: '1000px', height: '92vh' } as const;

/**
 * A draft's preview is the only place its report is saved, so it must not be dismissed by a stray click or Esc: the dialog
 * ignores those (disableClose) and the preview's own close buttons ask first (see ReportPdfPreview).
 */
export const REPORT_DRAFT_PREVIEW_DIALOG = { ...REPORT_PDF_PREVIEW_DIALOG, disableClose: true } as const;

/** The viewer opens fitted to the frame's width with its page-thumbnail sidebar closed, so the page gets all the room. */
export const REPORT_PDF_VIEW_PARAMS = '#navpanes=0&view=FitH';

/**
 * The report shown inline: a saved report as its pages with each iXBRL-tagged fact shaded (the old application's preview), a
 * draft as its PDF (a blob URL in an iframe, with REPORT_PDF_VIEW_PARAMS). For a draft
 * (BUG-048) it closes with the saved report once Save Report succeeds, and with nothing when it is closed unsaved.
 */
export function ReportPdfPreview({ data, dialogRef }: DialogComponentProps<ReportPdfPreviewData, ReportDetail>) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Bumped after a regeneration, so the preview shows the rebuilt report.
  const [version, setVersion] = useState(0);
  const saving = useRef(false);
  const confirming = useRef(false);
  const reportId = data?.reportId;
  const draft = data?.draft;

  useEffect(() => {
    if (!reportId && !draft) {
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    // A saved report previews as its pages with the iXBRL facts shaded (the old application's preview); a draft, not saved
    // yet, has no iXBRL and previews as its PDF.
    (draft ? reportsService.previewReportDraft(draft) : reportsService.previewReportHtml(reportId as string)).then(
      (blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(draft ? blob : new Blob([blob], { type: blob.type || 'text/html' }));
        // #159: a PDF opens fitted to the frame's width rather than the viewer's small default zoom.
        setPdfUrl(draft ? `${objectUrl}${REPORT_PDF_VIEW_PARAMS}` : objectUrl);
      },
      (error) => {
        if (cancelled) return;
        setLoadFailed(true);
        // A failed reload (after Regenerate) must not leave the previous document showing beside the failure.
        setPdfUrl(null);
        // Both the draft's PDF and the saved report's XHTML arrive as blobs, so a refusal's reason is read out of it.
        notifyBlobError(error, 'The report could not be previewed.');
      },
    );
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
    // The draft is fixed for the life of the dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, version]);

  const regenerate = async () => {
    if (await data?.onRegenerate?.()) {
      setLoadFailed(false);
      setVersion((v) => v + 1);
    }
  };

  const download = async () => {
    if (!reportId) {
      return;
    }
    try {
      await reportsService.downloadReport(reportId);
    } catch (error) {
      notifyBlobError(error, 'The report could not be downloaded.');
    }
  };

  /**
   * Closing a draft's preview saves nothing (BUG-048, as the old application), so before it closes - with its X or Esc - the
   * preparer is asked, because an edit made on the form is lost if it was only ever previewed.
   */
  const closePreview = async () => {
    if (!draft) {
      dialogRef.close(undefined);
      return;
    }
    if (confirming.current || saving.current) {
      return;
    }
    confirming.current = true;
    try {
      const leave = await openDialog<ConfirmPopupData, boolean>(ConfirmPopup, {
        data: {
          title: 'Close without saving?',
          message:
            'This report has not been saved yet, so your changes are lost if you close the preview. Click Cancel to go back, then Save Report to keep them.',
        },
      }).afterClosed();
      if (leave) {
        dialogRef.close(undefined);
      }
    } finally {
      confirming.current = false;
    }
  };

  // Esc closes a draft's preview like its X does (the dialog itself ignores Esc for a draft). Listened for while the key is
  // still travelling down (capture), because the dialog's own Escape handling stops it before it can bubble to the document.
  useEffect(() => {
    if (!draft) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        void closePreview();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  /** BUG-048: stores the report exactly as previewed. A refusal leaves the preview open with the reason. */
  const saveReport = async () => {
    if (!draft || saving.current) {
      return;
    }
    saving.current = true;
    setIsSaving(true);
    try {
      const detail = await reportsService.saveReport(draft);
      notifySuccess(NotificationTextMessage.successMessage);
      dialogRef.close(detail);
    } catch (error) {
      notifyError(error);
    } finally {
      saving.current = false;
      setIsSaving(false);
    }
  };

  return (
    <div className="mat-dialog-wrapper report-pdf-preview">
      <DialogContent>
        <div className="main-contain">
          <div className="sidenav-header">
            <div className="d-flex align-items-center justify-space-between">
              <h2>
                <span>{data?.refNo}</span>
              </h2>
              {draft ? (
                <div
                  role="button"
                  aria-label="Close"
                  tabIndex={0}
                  className="cursor-pointer"
                  onClick={() => void closePreview()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void closePreview();
                  }}
                >
                  <Icon name="close" className="close" />
                </div>
              ) : (
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              )}
            </div>
          </div>
        </div>

        {pdfUrl && (
          // The saved report's XHTML comes from the iXBRL service: sandboxed, so nothing in it can run. A draft's PDF is
          // not, as a sandboxed frame cannot show the browser's PDF viewer.
          // APT262: a fully locked-down sandbox ("") gives the frame an opaque, unique origin - and in that state a
          // browser's native same-document "#anchor" navigation only ever honours the first fragment click; every
          // later click to a different Contents entry is silently a no-op (reproduced: scrollTop froze across six
          // different anchors in a row, every one but the first). "allow-same-origin" restores the frame's real
          // (blob:) origin, which fixes that, while allow-scripts is still withheld, so nothing in the document runs.
          <iframe
            className="pdf-frame"
            src={pdfUrl}
            title="Report preview"
            sandbox={draft ? undefined : 'allow-same-origin'}
          ></iframe>
        )}
        {loadFailed && <p className="font-size-14">The report could not be previewed.</p>}
      </DialogContent>
      <DialogActions>
        {draft ? (
          <div className="d-flex align-items-center gap-15">
            <Button
              className="action-button primary-button"
              disabled={isSaving || loadFailed}
              onClick={() => void saveReport()}
            >
              Save Report
            </Button>
            <span className="font-size-14">
              This is a preview: your changes are kept only when you click Save Report.
            </span>
          </div>
        ) : (
          // As the old application's preview: Email on the left, PDF and Regenerate on the right.
          <div className="d-flex align-items-center justify-space-between gap-15 w-100">
            <div>
              {data?.onEmail && (
                <Button className="action-button secondary-button" onClick={() => void data.onEmail?.()}>
                  <Icon name="mail_outline" />
                  Email
                </Button>
              )}
            </div>
            <div className="d-flex gap-15">
              <Button className="action-button primary-button" title="Download PDF" onClick={() => void download()}>
                <Icon name="picture_as_pdf" outlined />
                PDF
              </Button>
              {data?.onRegenerate && (
                <Button className="action-button primary-button" onClick={() => void regenerate()}>
                  <Icon name="refresh" />
                  Regenerate
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogActions>
    </div>
  );
}
